import type { WhereSelection } from "@ui/components/SearchBar";
import { type ReactNode, useMemo, useState } from "react";
import { Configure } from "react-instantsearch";
import { useMobileMapConfig } from "@/hooks/use-mobile-map-config";
import { useSearchBoundsSync } from "@/hooks/use-search-bounds-sync";
import { useSearchMapPins } from "@/hooks/use-search-map-pins";
import { MobileSearchMap } from "@/layouts/components/mobile-search-map";
import { SearchMapSlot, useSearchResultsMap } from "@/layouts/map-host";
import type { Bbox } from "@/lib/where-bounding-boxes";
import { type WhereFilter, whereFilterToFacetFilters, whereFilterToTermsFilter } from "@/lib/where-regions";
import type { MapCluster } from "@/types/search-map";

export interface SearchMapPinsOptions {
  /** Extra msearch body the backend filters read (e.g. `_travily_when`).
   *  Caller-memoised — it is a `useSearchMapPins` effect dependency. */
  extraBody?: Record<string, unknown>;
  geoField: string;
  host: string;
  index: string;
  /** `_source` fields for single-listing pin popup cards. Module-hoisted —
   *  also a `useSearchMapPins` effect dependency. */
  sourceFields: string[];
}

export interface SearchMapSectionProps<THit, TId extends string | number> {
  /** The results column content (`SearchResultsBody` etc.). */
  children: ReactNode;
  /** Module-hoisted — identity feeds the map effects. */
  getHitId: (hit: THit) => TId;
  /** `SearchResultsHeading` node; rendered in the results column (desktop) or
   *  — absent `sheetHeading` — the sheet header (mobile). */
  heading: ReactNode;
  /** Compact `SearchResultsHeading variant="sheet"` node for the mobile sheet
   *  header (centered count per Figma). Falls back to `heading`. */
  sheetHeading?: ReactNode;
  /** Accessible label for the mobile sheet (the plain heading string). */
  headingLabel: string;
  /** "Map area" pill label for bounds-driven refinement. */
  mapAreaLabel: string;
  /** Map empty-state label. */
  mapEmptyLabel: string;
  markMapArea: (label: string) => void;
  pinIcon: ReactNode;
  /** Module-hoisted — identity feeds the map effects. */
  pinLabel: (cluster: MapCluster<THit>) => string | null;
  /** Caller-memoised object — identity feeds the pins effect. */
  pins: SearchMapPinsOptions;
  /** Caller-`useCallback`'d — identity feeds the map effects. */
  renderPin: (hit: THit, close: () => void, fullWidth?: boolean) => ReactNode;
  /** Cache-busting contexts for request-body params (`_travily_when`/`_travily_who`). */
  ruleContexts?: string[];
  whereBbox: Bbox | null;
  whereBoundsBox: string | null;
  /** Term narrowing for the Where selection — only indexes carrying `region`/`country_code`
   *  in their `filter_attributes` may pass one. */
  whereFilter?: WhereFilter | null;
  whereValue: WhereSelection | null;
}

/** The results+map scaffold shared by /destinations and /programs: bounds sync,
 *  full-match-set pin aggregation, pin selection/hover state, and the mobile
 *  sheet vs desktop two-column layout. The paginated card hits stay caller-owned
 *  (`useSearchResults` must not move in here — the map plots a separate
 *  server-side geo aggregation, and a second `useInfiniteHits` registration
 *  would double the widget). */
export function SearchMapSection<THit, TId extends string | number>({
  children,
  getHitId,
  heading,
  sheetHeading,
  headingLabel,
  mapAreaLabel,
  mapEmptyLabel,
  markMapArea,
  pinIcon,
  pinLabel,
  pins,
  renderPin,
  ruleContexts,
  whereBbox,
  whereBoundsBox,
  whereFilter = null,
  whereValue,
}: SearchMapSectionProps<THit, TId>) {
  const [selectedHitId, setSelectedHitId] = useState<TId | null>(null);
  const [hoveredHitId, setHoveredHitId] = useState<TId | null>(null);
  const { boundsBox, boundsSource, handleBoundsChange } = useSearchBoundsSync({
    whereBoundsBox,
    whereValue,
    whereFilter,
    markMapArea,
    mapAreaLabel,
  });
  const facetFilters = useMemo(() => whereFilterToFacetFilters(whereFilter), [whereFilter]);
  const whereTermsFilters = useMemo(() => {
    const clause = whereFilterToTermsFilter(whereFilter);
    return clause ? [clause] : [];
  }, [whereFilter]);
  // Derived, not read from state: `boundsBox` clears via an effect one render
  // after `whereFilter` arrives, and a query fired from that intermediate
  // render would AND a stale map bbox into the term filter. Deriving the pair
  // synchronously means no render can carry both.
  const effectiveBoundsBox = whereFilter ? null : boundsBox;

  // The map plots the FULL match set via a server-side geo aggregation, decoupled from the
  // paginated card `hits`. Mirroring `boundsBox` + the extra body keeps map and cards in step.
  const { useMobileLayout, fitPadding: mobileFitPad, selectionPadding: mobileSelectionPad } = useMobileMapConfig();
  const {
    points,
    isLoading: isPinsLoading,
    isError: isPinsError,
  } = useSearchMapPins<THit>({
    host: pins.host,
    index: pins.index,
    geoField: pins.geoField,
    sourceFields: pins.sourceFields,
    boundsBox: effectiveBoundsBox,
    extraBody: pins.extraBody,
    extraFilters: whereTermsFilters,
    // The map is visible on every breakpoint (mobile sheet included).
    enabled: true,
  });

  const { slotRef, selectedHit } = useSearchResultsMap<THit, TId>({
    points,
    getHitId,
    renderPin,
    pinIcon,
    pinLabel,
    selectedHitId,
    onSelectHit: setSelectedHitId,
    hoveredHitId,
    onHoverHit: setHoveredHitId,
    onUserBoundsChange: handleBoundsChange,
    isLoading: isPinsLoading,
    isError: isPinsError,
    emptyLabel: mapEmptyLabel,
    boundsSource,
    whereBbox,
    variant: useMobileLayout ? "sheet" : "inline",
    fitBoundsPadding: mobileFitPad,
    selectionPadding: mobileSelectionPad,
  });

  const pinCard = useMobileLayout && selectedHit ? renderPin(selectedHit, () => setSelectedHitId(null), true) : null;

  return (
    <>
      {/* Always mount Configure: with `preserveSharedStateOnUnmount` set on `<InstantSearch>`,
       *  unmounting the widget leaves the `insideBoundingBox`/`facetFilters` params in
       *  InstantSearch UI state and the filters survive the clear gesture. Passing `undefined`
       *  clears them cleanly. */}
      <Configure
        facetFilters={facetFilters}
        insideBoundingBox={effectiveBoundsBox ?? undefined}
        ruleContexts={ruleContexts}
      />

      {useMobileLayout ? (
        <MobileSearchMap
          header={sheetHeading ?? heading}
          label={headingLabel}
          mapSlotRef={slotRef}
          onDismissPin={() => setSelectedHitId(null)}
          pinCard={pinCard}
        >
          <div className="flex flex-col gap-6 pt-2">{children}</div>
        </MobileSearchMap>
      ) : (
        // 1440px Figma: cards 788, map 560 (ratio 788:560 ≈ 1.4:1). Both columns scale.
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-6 p-5 md:p-8 lg:pt-6">
            {heading}
            {children}
          </div>
          <SearchMapSlot slotRef={slotRef} />
        </div>
      )}
    </>
  );
}
